import rison from 'rison';

import type { IntegrationCategory } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import { arrayOfAll, filterOutNullable, findInArray } from '@gitbook/util-ts';

import {
    parseIntegrationCategoryParam,
    parseIntegrationModalStateParam,
    parseIntegrationsProviderParam,
} from './parse-url-params';
import {
    COLLECTION_SIDESHEET_KEYS,
    getNavigationSideSheetTabs,
    getSpaceSideSheetKeys,
    type NavigationAuditsSideSheet,
    type NavigationCommentsSideSheet,
    type NavigationSideSheetType,
} from './side-sheets';
import {
    BillingQueryParams,
    type LocationDescriptor,
    type NavigationScreenAccountSettings,
    type NavigationScreenAgentsState,
    type NavigationScreenChangeRequestsFilters,
    type NavigationScreenChangeRequestsState,
    type NavigationScreenCollectionState,
    type NavigationScreenIntegrationState,
    type NavigationScreenOpenAPISpecState,
    type NavigationScreenOpenAPISpecsHomeState,
    type NavigationScreenOrgSettings,
    type NavigationScreenSiteState,
    type NavigationScreenSitesListState,
    type NavigationScreenSpaceState,
    type NavigationScreenState,
    type NavigationScreenTranslationState,
    NavigationSiteCustomizationTabs,
    NavigationSiteInsightsReports,
    NavigationSiteSettingsSections,
} from './types';
import { normalizePath, type PathMatch, switcher } from './utils';

// Regex to match a page
export const MATCH_PAGE = '([^@~]*)';

interface ParsedLocationDescriptor {
    pathname: string;
    query: URLSearchParams;
}

/**
 * Compute the navigation state from a location.
 */
export function resolveLocationToNavigationScreen(
    location: LocationDescriptor
): NavigationScreenState {
    const query = new URLSearchParams(location.search);

    const screen = { type: 'home' } as NavigationScreenState;
    screenSwitcher(screen, { pathname: normalizePath(location.pathname), query });

    if (location.hash) {
        // Location may include the leading # sometimes
        screen.hash = location.hash.replace(/^#/, '');
    }

    return { ...screen, ...extractGlobalParamsFromLocation(query) };
}

/**
 * Extract some global params from the query.
 */
function extractGlobalParamsFromLocation(
    queryParameters: URLSearchParams
): Pick<NavigationScreenState, 'askAI' | 'query' | 'ssoLogin'> {
    const no_sso = queryParameters.get('no_sso');
    const email = queryParameters.get('email');
    const ask_ai = queryParameters.get('ask_ai');
    return {
        query: queryParameters.has('q') ? queryParameters.get('q') || '' : undefined,
        askAI: ask_ai ? ['true', '1'].includes(ask_ai) || undefined : undefined,
        ssoLogin:
            no_sso || email
                ? {
                      noSSO: no_sso === 'account_exists' ? no_sso : undefined,
                      email: email || undefined,
                  }
                : undefined,
    };
}

function getSitesParams(location: ParsedLocationDescriptor) {
    return {
        type: 'sites' as const,
        /** Site published status. */
        status: findInArray(location.query.get('status'), ['published', 'unpublished'] as const),
        /** Search query. */
        search: location.query.get('search') || undefined,
    };
}

function getIntegrationsParams(
    location: ParsedLocationDescriptor,
    submatch?: PathMatch<{ org: string }>
) {
    return {
        type: 'integrations',
        organization: submatch?.params.org,
        /** Category filter */
        category: parseIntegrationCategoryParam(location.query.get('category')),
        /** Search query */
        search: location.query.get('search') || undefined,
        /** Provider of the integration: marketplace or current org */
        provider: parseIntegrationsProviderParam(location.query.get('provider')),
    };
}

/**
 * Sub-switcher to match the inbox state. Mutates the given context
 */
function screenSwitcher(screen: NavigationScreenState, location: ParsedLocationDescriptor): void {
    switcher(location.pathname, [
        // Login with basename
        {
            path: '/@:basename/',
            onMatch: (submatch: PathMatch<{ basename: string }>) => {
                Object.assign(screen, {
                    type: 'org_with_basename',
                    organizationBaseName: submatch.params.basename,
                });
            },
        },
        {
            path: '/login/:basename',
            onMatch: (submatch: PathMatch<{ basename: string }>) => {
                Object.assign(screen, {
                    type: 'org_with_basename',
                    organizationBaseName: submatch.params.basename,
                });
            },
        },
        // Invites
        {
            path: '/invite/:org/:invite',
            onMatch: (match: PathMatch<{ org: string; invite: string }>) => {
                Object.assign(screen, {
                    type: 'org_join',
                    organization: match.params.org,
                    invite: match.params.invite,
                });
            },
        },
        {
            path: '/account',
            onMatch: (submatch) => {
                Object.assign(screen, {
                    type: 'account',
                    tab: 'profile',
                });
                accountSettingsSwitcher(
                    screen as NavigationScreenAccountSettings,
                    submatch,
                    location
                );
            },
        },
        {
            path: '/u/:user',
            onMatch: (submatch: PathMatch<{ user: string }>) => {
                Object.assign(screen, {
                    type: 'user',
                    user: submatch.params.user,
                });
            },
        },
        {
            path: '/integrations/:integration',
            onMatch: (submatch: PathMatch<{ integration: string }>) => {
                Object.assign(screen, {
                    type: 'integration',
                    integration: submatch.params.integration,
                    tab: 'about',
                });
                integrationScreenTabSwitcher(
                    screen as NavigationScreenIntegrationState,
                    submatch,
                    location
                );
            },
        },
        {
            path: '/o/:org/integrations/:integration',
            onMatch: (submatch: PathMatch<{ org: string; integration: string }>) => {
                Object.assign(screen, {
                    type: 'integration',
                    organization: submatch.params.org,
                    integration: submatch.params.integration,
                    tab: 'about',
                });
                integrationScreenTabSwitcher(
                    screen as NavigationScreenIntegrationState,
                    submatch,
                    location
                );
            },
        },
        {
            path: '/integrations',
            onMatch: () => {
                Object.assign(screen, getIntegrationsParams(location));
            },
        },
        {
            path: '/o/:org/integrations',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, getIntegrationsParams(location, submatch));
            },
        },
        {
            path: '/o/:org/settings',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const baseScreen: NavigationScreenOrgSettings = {
                    type: 'org_settings',
                    tab: { type: 'profile' },
                    organization: submatch.params.org,
                };

                Object.assign(screen, baseScreen);
                orgSettingsSwitcher(screen as NavigationScreenOrgSettings, submatch, location);
            },
        },
        {
            path: '/o/:org/invite',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'org_invite',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/sites/:site',
            onMatch: (submatch: PathMatch<{ site: string }>) => {
                Object.assign(screen, {
                    type: 'site',
                    site: submatch.params.site,
                });

                siteSwitcher(screen as NavigationScreenSiteState, submatch, location);
            },
        },
        {
            path: '/o/:org/sites/:site',
            onMatch: (submatch: PathMatch<{ org: string; site: string }>) => {
                Object.assign(screen, {
                    type: 'site',
                    site: submatch.params.site,
                    organization: submatch.params.org,
                });

                siteSwitcher(screen as NavigationScreenSiteState, submatch, location);
            },
        },
        {
            path: '/sites',
            onMatch: () => {
                Object.assign<NavigationScreenState, NavigationScreenSitesListState>(
                    screen,
                    getSitesParams(location)
                );
            },
        },
        {
            path: '/o/:org/sites',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign<NavigationScreenState, NavigationScreenSitesListState>(screen, {
                    ...getSitesParams(location),
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/s/:space',
            onMatch: (submatch: PathMatch<{ space: string }>) => {
                Object.assign(screen, {
                    type: 'space',
                    space: submatch.params.space,
                    page: '',
                });

                spaceSwitcher(screen as NavigationScreenSpaceState, submatch, location);
            },
        },
        {
            path: '/o/:org/s/:space',
            onMatch: (submatch: PathMatch<{ org: string; space: string }>) => {
                Object.assign(screen, {
                    type: 'space',
                    space: submatch.params.space,
                    organization: submatch.params.org,
                    page: '',
                });

                spaceSwitcher(screen as NavigationScreenSpaceState, submatch, location);
            },
        },
        {
            path: '/o/:org/c/:collection',
            onMatch: (submatch: PathMatch<{ org: string; collection: string }>) => {
                Object.assign(screen, {
                    type: 'collection',
                    collection: submatch.params.collection,
                    organization: submatch.params.org,
                    page: '',
                });

                collectionSideSheetSwitcher(
                    screen as NavigationScreenCollectionState,
                    submatch,
                    location
                );
            },
        },
        {
            path: '/openapi/:spec',
            onMatch: (submatch: PathMatch<{ org: string; spec: string }>) => {
                const openAPISpecScreen: NavigationScreenOpenAPISpecState = {
                    type: 'openapi_spec',
                    organization: undefined,
                    spec: submatch.params.spec,
                };
                Object.assign(screen, openAPISpecScreen);
            },
        },
        {
            path: '/openapi',
            onMatch: () => {
                const openAPISpecListScreen: NavigationScreenOpenAPISpecsHomeState = {
                    type: 'openapi_specs',
                    organization: undefined,
                };
                Object.assign(screen, openAPISpecListScreen);
            },
        },
        {
            path: '/o/:org/openapi/:spec',
            onMatch: (submatch: PathMatch<{ org: string; spec: string }>) => {
                const openAPISpecListScreen: NavigationScreenOpenAPISpecState = {
                    type: 'openapi_spec',
                    organization: submatch.params.org,
                    spec: submatch.params.spec,
                };
                Object.assign(screen, openAPISpecListScreen);
            },
        },
        {
            path: '/o/:org/openapi',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const openAPISpecListScreen: NavigationScreenOpenAPISpecsHomeState = {
                    type: 'openapi_specs',
                    organization: submatch.params.org,
                };
                Object.assign(screen, openAPISpecListScreen);
            },
        },

        // Change requests
        {
            path: '/o/:org/change-requests/s/:spaceId/c/:changeRequestNumber',
            onMatch: (
                submatch: PathMatch<{ org: string; spaceId: string; changeRequestNumber: string }>
            ) => {
                const baseChangeRequestsScreen: NavigationScreenChangeRequestsState = {
                    type: 'change_requests',
                    organization: submatch.params.org,
                    filters: extractChangeRequestsFilterParams(location),
                    selected: {
                        spaceId: submatch.params.spaceId,
                        changeRequestNumber: parseInt(submatch.params.changeRequestNumber, 10),
                    },
                };
                const selected = {
                    spaceId: submatch.params.spaceId,
                    changeRequestNumber: parseInt(submatch.params.changeRequestNumber, 10),
                    pageId: location.query.get('page') ?? undefined,
                };

                commentsSwitcher(
                    submatch,
                    location,
                    (sideSheet) => {
                        Object.assign(screen, {
                            ...baseChangeRequestsScreen,
                            selected: {
                                ...selected,
                                sideSheet,
                            },
                        } satisfies NavigationScreenChangeRequestsState);
                    },
                    (sideSheetSubMatch) => {
                        switcher(
                            location.pathname,
                            [
                                {
                                    path: '/~/agent',
                                    onMatch: () => {
                                        Object.assign(screen, {
                                            ...baseChangeRequestsScreen,
                                            selected: {
                                                ...selected,
                                                sideSheet: {
                                                    type: 'agent',
                                                },
                                            },
                                        } satisfies NavigationScreenChangeRequestsState);
                                    },
                                },
                                {
                                    path: '',
                                    onMatch: () => {
                                        Object.assign(screen, {
                                            ...baseChangeRequestsScreen,
                                            selected,
                                        } satisfies NavigationScreenChangeRequestsState);
                                    },
                                },
                            ],
                            { prefix: sideSheetSubMatch.path }
                        );
                    }
                );
            },
        },
        {
            path: '/o/:org/change-requests',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const changeRequestsScreen: NavigationScreenChangeRequestsState = {
                    type: 'change_requests',
                    organization: submatch.params.org,
                    filters: extractChangeRequestsFilterParams(location),
                };
                Object.assign(screen, changeRequestsScreen);
            },
        },

        // Agents
        {
            path: '/o/:org/agents',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const agentsListScreen: NavigationScreenAgentsState = {
                    type: 'agents',
                    organization: submatch.params.org,
                };
                Object.assign(screen, agentsListScreen);
            },
        },

        // Translations
        {
            path: '/translations',
            onMatch: () => {
                Object.assign(screen, {
                    type: 'translations',
                    view: 'overview',
                });
            },
        },
        {
            path: '/o/:org/translations/glossary',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const translationListScreen: NavigationScreenTranslationState = {
                    type: 'translations',
                    organization: submatch.params.org,
                    view: 'glossary',
                };
                Object.assign(screen, translationListScreen);
            },
        },
        {
            path: '/o/:org/translations',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const translationListScreen: NavigationScreenTranslationState = {
                    type: 'translations',
                    organization: submatch.params.org,
                    view: 'overview',
                };
                Object.assign(screen, translationListScreen);
            },
        },

        {
            path: '/o/:org/trash',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'trash',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/o/:org/home',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'home',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/o/:org',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'root',
                    organization: submatch.params.org,
                });
            },
        },

        // Welcome
        {
            path: '/welcome',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                });
            },
        },
        {
            path: '/welcome/o/business',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                    organizationType: 'business',
                });
            },
        },
        {
            path: '/welcome/o/community',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                    organizationType: 'community',
                });
            },
        },
        {
            path: '/c/:collection',
            onMatch: (submatch: PathMatch<{ collection: string }>) => {
                Object.assign(screen, {
                    type: 'collection',
                    collection: submatch.params.collection,
                });

                collectionSideSheetSwitcher(
                    screen as NavigationScreenCollectionState,
                    submatch,
                    location
                );
            },
        },

        // Root, Join & home
        {
            path: '/home',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'home',
                });
            },
        },
        {
            path: '/join',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'join',
                });
            },
        },
        {
            path: '/',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'root',
                });
            },
        },
        {
            path: '/',
            onMatch: () => {
                Object.assign(screen, {
                    type: 'error_notfound',
                });
            },
        },
    ]);
}

/**
 * Sub-switcher for the integration screen
 */
function integrationScreenTabSwitcher(
    screen: NavigationScreenIntegrationState,
    match,
    location: ParsedLocationDescriptor
): void {
    const matchTab = (tab: string) => {
        const sanitizedTab = findInArray(tab, ['about', 'installations'] as const);
        screen.tab = sanitizedTab ?? 'about';
    };

    const matchInstallationFlow = (installationState: {
        step: string;
        context?: {
            type: string;
            id: string;
        };
    }) => {
        // installation flow initial step
        const step = findInArray(installationState?.step, ['flow', 'configure'] as const);
        const stepState = { step: step ?? 'flow' };
        let contextState = {};

        // installation flow context (optional)
        if (installationState?.context) {
            const contextType = findInArray(installationState.context.type, [
                'site',
                'space',
            ] as const);

            if (contextType) {
                contextState = {
                    context: {
                        type: contextType,
                        id: installationState.context.id,
                    },
                };
            }
        }

        screen.installation = {
            ...stepState,
            ...contextState,
        };
    };

    switcher(
        location.pathname,
        [
            {
                /**
                 * Installation flow URL
                 *  -- installationStep: flow | configure
                 *  -- contextType: site | space
                 *  -- contextId: siteId | spaceId
                 */
                path: '/:tab?/~/installation/:installationStep?/:contextType?/:contextId?',
                onMatch: (
                    submatch: PathMatch<{
                        tab: string;
                        installationStep: string;
                        contextType: string;
                        contextId: string;
                    }>
                ) => {
                    matchTab(submatch.params.tab);
                    matchInstallationFlow({
                        step: submatch.params.installationStep,
                        context: {
                            type: submatch.params.contextType,
                            id: submatch.params.contextId,
                        },
                    });
                },
            },
            {
                path: '/:tab',
                onMatch: (submatch: PathMatch<{ tab: string; installationStep: string }>) => {
                    matchTab(submatch.params.tab);
                },
            },
            {
                path: '',
                onMatch: () => {
                    screen.tab = 'about';
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for the integrations modal.
 */
function integrationsModalSwitcher(
    screen: NavigationScreenSiteState | NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/installations',
                onMatch: () => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'installations',
                        },
                    };
                },
            },
            {
                path: '/category/:category',
                onMatch: (submatch: PathMatch<{ category: IntegrationCategory | undefined }>) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'category',
                            category: parseIntegrationCategoryParam(submatch.params.category),
                            /** Search query */
                            search: location.query.get('search') || undefined,
                            /** Provider of the integration: marketplace or current org */
                            provider: parseIntegrationsProviderParam(
                                location.query.get('provider')
                            ),
                        },
                    };
                },
            },
            {
                path: '/:integration/:state',
                onMatch: (
                    submatch: PathMatch<{ integration: string; state: 'configure' | undefined }>
                ) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'integration',
                            integration: submatch.params.integration,
                            state: parseIntegrationModalStateParam(submatch.params.state),
                        },
                    };
                },
            },
            {
                path: '/:integration',
                onMatch: (submatch: PathMatch<{ integration: string }>) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: { type: 'integration', integration: submatch.params.integration },
                    };
                },
            },
            {
                path: '',
                onMatch: () => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'installations',
                        },
                    };
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match a site.
 */
function siteSwitcher(
    screen: NavigationScreenSiteState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    siteSideSheetSwitcher(screen, match, location);
    switcher(
        location.pathname,
        [
            {
                path: '/insights/:report?',
                onMatch: (submatch) => {
                    const rawFilters = location.query.get('filters');
                    let filters: any;

                    try {
                        filters = rawFilters ? rison.decode_object(rawFilters) : undefined;
                    } catch (_error) {}

                    screen.tab = {
                        type: 'insights',
                        report:
                            findInArray(submatch.params.report, NavigationSiteInsightsReports) ??
                            'traffic',
                        filters,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/settings/:section',
                onMatch: (submatch) => {
                    const section = findInArray(
                        submatch.params.section,
                        NavigationSiteSettingsSections
                    );
                    screen.tab = { type: 'settings', section };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/preview',
                onMatch: (submatch) => {
                    screen.tab = { type: 'preview' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/customization/v/:siteSpaceId/:tab',
                onMatch: (submatch) => {
                    const tab = findInArray(submatch.params.tab, NavigationSiteCustomizationTabs);
                    screen.tab = {
                        type: 'customization',
                        tab: tab ?? 'general',
                        siteSpaceId: submatch.params.siteSpaceId,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/customization/:tab',
                onMatch: (submatch) => {
                    const tab = findInArray(submatch.params.tab, NavigationSiteCustomizationTabs);
                    screen.tab = { type: 'customization', tab: tab ?? 'general' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );

    // Extract any params within the Site scope
    const billingParam = findInArray(
        location.query.get('billing'),
        Object.values(BillingQueryParams)
    );

    if (billingParam) {
        screen.billing = billingParam;
    }
}

function siteSideSheetSwitcher(
    screen: NavigationScreenSiteState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/integrations',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'integrations' };
                    integrationsModalSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/audience',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'audience' };
                    sideSheetTabSwitcher(screen, submatch, location, 'audience');
                },
            },
            {
                path: '/~/publish',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'publish' };
                    sideSheetTabSwitcher(screen, submatch, location, 'publish');
                },
            },
            {
                path: '/~/published',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'published' };
                    sideSheetTabSwitcher(screen, submatch, location, 'published');
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match a space.
 */
function spaceSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/diff',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'diff',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/preview',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'preview',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    spaceContentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to the content at a space or change request level.
 */
function contentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/revisions/:revision',
                onMatch: (submatch: PathMatch<{ revision: string }>) => {
                    Object.assign(screen, {
                        revision: submatch.params.revision,
                    });

                    reusableContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch: PathMatch<{ changeRequest: string }>) => {
                    reusableContentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for reusable content of a space.
 */
/**
 * Sub-switcher to the content at a space or change request level.
 */
function reusableContentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: `/~/reusable/:reusable/:page${MATCH_PAGE}`,
                onMatch: (submatch: PathMatch<{ reusable: string; page: string }>) => {
                    Object.assign(screen, {
                        reusableContent: submatch.params.reusable,
                        page: submatch.params.page,
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/reusable/:reusable',
                onMatch: (submatch: PathMatch<{ reusable: string }>) => {
                    Object.assign(screen, {
                        reusableContent: submatch.params.reusable,
                        page: '',
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: `/:page${MATCH_PAGE}`,
                onMatch: (submatch: PathMatch<{ page: string }>) => {
                    Object.assign(screen, {
                        page: submatch.params.page,
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch: PathMatch) => {
                    Object.assign(screen, {
                        page: '',
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for the content of space.
 */
function spaceContentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/changes/:changeRequest',
                onMatch: (submatch: PathMatch<{ changeRequest: string }>) => {
                    Object.assign(screen, {
                        changeRequest: parseChangeRequestIdentifier(submatch.params.changeRequest),
                    });

                    contentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    contentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match a comments side-panel.
 */
function commentsSwitcher(
    match: PathMatch,
    location: ParsedLocationDescriptor,
    onMatch: (sideSheet: NavigationCommentsSideSheet) => void,
    onFallbackMatch: (submatch: PathMatch) => void
): void {
    switcher(
        location.pathname,
        [
            /**
             * @deprecated
             * This is a legacy route that we need to support for backwards compatibility
             */
            {
                path: '/~/c/:node/:comment?',
                onMatch: (submatch: PathMatch<{ node: string; comment?: string }>) => {
                    onMatch({
                        type: 'comments',
                        node: submatch.params.node,
                        comment: submatch.params.comment,
                    });
                },
            },
            /**
             * @deprecated
             * This is a legacy route that we need to support for backwards compatibility
             */
            {
                path: '/~/comments/:tab/~c/:comment',
                onMatch: (submatch: PathMatch<{ comment: string }>) => {
                    onMatch({
                        type: 'comments',
                        comment: submatch.params.comment,
                    });
                },
            },
            {
                path: '/~/comments/:comment?',
                onMatch: (submatch: PathMatch<{ comment?: string }>) => {
                    const comment = submatch.params.comment;
                    const context = findInArray(location.query.get('context'), [
                        'preview',
                    ] as const);
                    const node = location.query.get('node');
                    const record = location.query.get('record');
                    const definition = location.query.get('definition');

                    onMatch({
                        type: 'comments',
                        ...(comment ? { comment } : {}),
                        ...(context ? { context } : {}),
                        ...(node ? { node } : {}),
                        ...(record && definition ? { tableCell: { record, definition } } : {}),
                    });
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    onFallbackMatch(submatch);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Match comments side-sheet in a space
 */
function spaceCommentsSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
) {
    commentsSwitcher(
        match,
        location,
        (sideSheet) => {
            screen.sideSheet = sideSheet;
        },
        (submatch) => {
            spaceSideSheetSwitcher(screen, submatch, location);
        }
    );
}

/**
 * Sub-switcher to match the sidesheet in a collection
 */
function collectionSideSheetSwitcher(
    screen: NavigationScreenCollectionState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        COLLECTION_SIDESHEET_KEYS.map((sideSheet) => ({
            path: `/~/${sideSheet}`,
            onMatch: (submatch) => {
                screen.sideSheet = { type: sideSheet };

                sideSheetTabSwitcher(screen, submatch, location, sideSheet);
            },
        })),
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match the sidesheet in a space
 */
function spaceSideSheetSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    const keys = getSpaceSideSheetKeys(screen);

    switcher(
        location.pathname,
        keys.map((sideSheet) => ({
            path: `/~/${sideSheet}`,
            onMatch: (submatch) => {
                screen.sideSheet = { type: sideSheet };

                if (sideSheet === 'integrations') {
                    integrationsModalSwitcher(screen, submatch, location);
                } else if (sideSheet === 'audits') {
                    brokenLinksAuditExtractor(
                        screen.sideSheet as NavigationAuditsSideSheet,
                        location
                    );
                } else if (sideSheet === 'change-requests') {
                    changeRequestsSideSheetSwitcher(screen, submatch, location);
                } else {
                    sideSheetTabSwitcher(screen, submatch, location, sideSheet);
                }
            },
        })),
        { prefix: match.path }
    );
}

function changeRequestsSideSheetSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        arrayOfAll<api.ChangeRequestStatus>()([
            api.ChangeRequestStatus.Draft,
            api.ChangeRequestStatus.Open,
            api.ChangeRequestStatus.Merged,
            api.ChangeRequestStatus.Archived,
        ]).map((status) => ({
            path: `/${status}`,
            onMatch: () => {
                screen.sideSheet = {
                    type: 'change-requests',
                    filters: {
                        ...extractChangeRequestsFilterParams(location),
                        status,
                    },
                };
            },
        })),
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match the tab of a sidesheet
 */
function sideSheetTabSwitcher(
    screen:
        | NavigationScreenSpaceState
        | NavigationScreenCollectionState
        | NavigationScreenSiteState,
    match: PathMatch,
    location: ParsedLocationDescriptor,
    sideSheetKey: NavigationSideSheetType
): void {
    const tabs = getNavigationSideSheetTabs(sideSheetKey);

    if (tabs.length) {
        switcher(
            location.pathname,
            tabs
                .map((tab) =>
                    tab
                        ? {
                              path: `/${tab}`,
                              onMatch: () => {
                                  screen.sideSheet = screen.sideSheet
                                      ? Object.assign(screen.sideSheet, { tab })
                                      : undefined;
                              },
                          }
                        : undefined
                )
                .filter(filterOutNullable),
            { prefix: match.path }
        );
    }
}

/**
 * Extract the filtering parameters for the broken links sidesheet.
 */
function brokenLinksAuditExtractor(
    sideSheet: NavigationAuditsSideSheet,
    location: ParsedLocationDescriptor
) {
    sideSheet.node = location.query.get('node') ?? undefined;

    const scope = findInArray(location.query.get('scope'), ['change-request', 'space'] as const);
    const status = findInArray(location.query.get('status'), [
        api.ContentReferenceStatus.Broken,
        api.ContentReferenceStatus.InApp,
    ]);

    if (scope) {
        sideSheet.scope = scope;
    }

    if (status) {
        sideSheet.status = status;
    }
}

/**
 * Sub-switcher to match the tabs in account settings
 */
function accountSettingsSwitcher(
    screen: NavigationScreenAccountSettings,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/profile',
                onMatch: () => {
                    screen.tab = 'profile';
                },
            },
            {
                path: '/developer',
                onMatch: () => {
                    screen.tab = 'developer';
                },
            },
            {
                path: '/organizations',
                onMatch: () => {
                    screen.tab = 'organizations';
                },
            },
            {
                path: '/notification',
                onMatch: () => {
                    screen.tab = 'notification';
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match the tabs in organization settings
 */
function orgSettingsSwitcher(
    screen: NavigationScreenOrgSettings,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/billing',
                onMatch: () => {
                    const rawReturnUrl = location.query.get('returnScreen');
                    const publishSiteId = location.query.get('publishSiteId');
                    const changeSiteTypeParam = location.query.get('changeSiteType');
                    const changeSiteType = changeSiteTypeParam
                        ? (() => {
                              const [siteId, siteType] = changeSiteTypeParam.split(':');
                              return siteId && siteType ? { siteId, siteType } : undefined;
                          })()
                        : undefined;
                    screen.tab = {
                        type: 'billing',
                        ...(rawReturnUrl
                            ? {
                                  returnScreen: resolveLocationToNavigationScreen({
                                      pathname: decodeURIComponent(rawReturnUrl),
                                  }),
                              }
                            : {}),
                        ...(publishSiteId ? { publishSiteId } : {}),
                        ...(changeSiteType ? { changeSiteType } : {}),
                    };
                },
            },
            {
                path: '/upgrade',
                onMatch: () => {
                    screen.tab = {
                        type: 'upgrade',
                    };
                },
            },
            {
                path: '/members/:memberId/:detailsTab',
                onMatch: (submatch: PathMatch<{ memberId: string; detailsTab: string }>) => {
                    const memberId = submatch.params.memberId;
                    const detailsTab = submatch.params.detailsTab;

                    screen.tab = {
                        type: 'members',
                        memberId,
                        detailsTab,
                    };
                },
            },
            {
                path: '/members/:memberId?',
                onMatch: (submatch: PathMatch<{ memberId?: string }>) => {
                    const memberId = submatch.params.memberId;

                    screen.tab = {
                        type: 'members',
                        memberId,
                    };
                },
            },
            {
                path: '/teams/:teamId/:detailsTab',
                onMatch: (submatch: PathMatch<{ teamId: string; detailsTab: string }>) => {
                    const teamId = submatch.params.teamId;
                    const detailsTab = submatch.params.detailsTab;

                    screen.tab = {
                        type: 'teams',
                        teamId,
                        detailsTab,
                    };
                },
            },
            {
                path: '/teams/:teamId?',
                onMatch: (submatch: PathMatch<{ teamId?: string }>) => {
                    const teamId = submatch.params.teamId;

                    screen.tab = {
                        type: 'teams',
                        teamId,
                    };
                },
            },
            {
                path: '/sso',
                onMatch: () => {
                    screen.tab = { type: 'sso' };
                },
            },
            {
                path: '/invites',
                onMatch: () => {
                    screen.tab = { type: 'invites' };
                },
            },
            {
                path: '/rules',
                onMatch: () => {
                    screen.tab = { type: 'rules' };
                },
            },
            {
                path: '/agents/:tab?',
                onMatch: (submatch: PathMatch<{ tab?: string }>) => {
                    screen.tab = {
                        type: 'agents',
                        tab: parseAgentSettingsTab(submatch.params.tab),
                    };
                },
            },
            {
                path: '/plans',
                onMatch: () => {
                    const rawInterval = location.query.get('interval');
                    screen.tab = {
                        type: 'plans',
                        interval:
                            rawInterval === 'monthly' || rawInterval === 'yearly'
                                ? rawInterval
                                : undefined,
                    };
                },
            },
            {
                path: '',
                onMatch: () => {
                    screen.tab = { type: 'profile' };
                },
            },
        ],
        { prefix: match.path }
    );
}

const AGENT_SETTINGS_TABS = ['connectors', 'data', 'settings'] as const;
type AgentSettingsTab = (typeof AGENT_SETTINGS_TABS)[number];

function parseAgentSettingsTab(rawTab?: string | null): AgentSettingsTab {
    return (AGENT_SETTINGS_TABS as readonly string[]).includes(rawTab ?? '')
        ? (rawTab as AgentSettingsTab)
        : 'connectors';
}

function extractChangeRequestsFilterParams(
    location: ParsedLocationDescriptor
): NavigationScreenChangeRequestsFilters {
    const status = findInArray(location.query.get('status'), [
        'draft',
        'open',
        'merged',
        'archived',
    ] as const) as api.ChangeRequestStatus | undefined;
    const contributor = location.query.get('contributor') ?? undefined;
    const requestedReview = location.query.get('requestedReview') ?? undefined;
    const conversationsIssuesCluster =
        location.query.get('conversationsIssuesCluster') ?? undefined;
    const creator = location.query.get('creator') ?? undefined;
    const space = location.query.get('space') ?? undefined;

    return {
        ...(status ? { status } : {}),
        ...(contributor ? { contributor } : {}),
        ...(creator ? { creator } : {}),
        ...(conversationsIssuesCluster ? { conversationsIssuesCluster } : {}),
        ...(requestedReview ? { requestedReview } : {}),
        ...(space ? { space } : {}),
    };
}

/**
 * Parse the change request identifier from the url into either an ID or a number.
 */
function parseChangeRequestIdentifier(input: string): string | number {
    if (/^\d+$/.test(input)) {
        return Number.parseInt(input, 10);
    }
    return input;
}
